import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Anchor from 'components/GlobalStyles/Anchor'
import FlaredArrowShort from 'components/SVG/FlaredArrowShort'

import { RouteHistoryContext } from 'services/contexts'

import * as T from 'types'
import { BreadcrumbEnum } from 'types/enums'

import Spacer from './Spacer'

interface IContainerProps {
  noMargin?: boolean
  hideMobile?: boolean
}

const Container = styled.div<IContainerProps>`
  margin-left: 15px;
  margin-top: 15px;
  ${props => props.noMargin && 'margin: 0;'}
  display: ${props => (props.hideMobile ? 'none' : 'flex')};
  align-items: center;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: flex;
    margin-left: 30px;
    ${props => props.noMargin && 'margin: 0;'}
  }
`

interface ICurrentPageProps {
  currentPage?: boolean
}
const StyledLink = styled.span<ICurrentPageProps>`
  padding-top: 2px; /* font correction */
  font-style: normal;
  font-weight: ${props => (props.currentPage ? 900 : 500)};
  font-size: 16px;
  line-height: 20px;
  text-decoration: none;
  color: ${props => props.theme.colors.purple};
  cursor: ${props => (props.currentPage ? 'cursor' : 'pointer')};

  &:visited {
    color: ${props => props.theme.colors.purple};
  }
`

const Slash = styled.div`
  height: 21.73px; /* 8.5w x 20h */
  width: 1px;
  background: ${props => props.theme.colors.grey3};
  transform: rotate(23deg);
  margin: 0 15px;
`

const LeftFlaredArrowShort = styled(FlaredArrowShort)`
  transform: rotate(180deg);
`

interface IProps {
  noMargin?: boolean
  hideMobile?: boolean
  name: BreadcrumbEnum
  listingName?: string
}

const Breadcrumbs: FunctionComponent<IProps> = ({ noMargin, hideMobile, name, listingName }) => {
  const theme = useContext(ThemeContext)

  const router = useRouter()

  const routeHistory = useContext(RouteHistoryContext)
  const [pages, setPages] = useState<T.IRoutePath[]>(routeHistory.paths)

  const breadcrumbPages: { [key: string]: BreadcrumbEnum } = {
    '/how-it-works': BreadcrumbEnum.HowItWorks,
    '/pricing': BreadcrumbEnum.Pricing,
    '/faq': BreadcrumbEnum.FAQ,
    '/listings/homes-for-sale': BreadcrumbEnum.HomesForSale,
    '/listings/sold': BreadcrumbEnum.RecentSales,
    '/listing/[id]': BreadcrumbEnum.ViewListing,
  }

  useEffect(() => {
    const pageName = listingName || String(breadcrumbPages[router.pathname])
    const path = { name: pageName, pathname: router.pathname, asPath: router.asPath }

    // different breadcumb name page
    if (routeHistory.name && routeHistory.name !== name) {
      routeHistory.reset(name)
      routeHistory.push(path, name)
      return
    }

    // same breadcrumb name with new route path
    const lastRoute = routeHistory.paths[routeHistory.paths.length - 1]

    if (lastRoute && lastRoute.pathname !== router.pathname) {
      // do not push another route when lastRoute was view visting route (fix for browser back
      // button that is not detected by the routeHistory context
      if (lastRoute.pathname === '/listing/[id]') {
        routeHistory.popLast()
        return
      }

      routeHistory.push(path, name)
    }
  }, [])

  useEffect(() => {
    setPages(routeHistory.paths)
  }, [routeHistory.paths])

  if (pages.length === 0) return null

  return (
    <Container
      noMargin={noMargin}
      hideMobile={hideMobile}
    >
      <LeftFlaredArrowShort
        width="20"
        height="20"
        fill={theme.colors.purple}
      />
      <Spacer size={15} />
      {pages.map((page, idx) => (
        <Fragment key={page.pathname}>
          {idx === pages.length - 1
            ? (
              <StyledLink currentPage={idx === pages.length - 1}>
                <Anchor>{page.name}</Anchor>
              </StyledLink>
            )
            : (
              <Link
                href={page.pathname}
                as={page.asPath}
                passHref
              >
                <StyledLink
                  currentPage={idx === pages.length - 1}
                  onClick={() => routeHistory.backTo(idx, name)}
                >
                  <Anchor>{page.name}</Anchor>
                </StyledLink>
              </Link>
            )}
          {idx < pages.length - 1 && <Slash />}
        </Fragment>
      ))}
    </Container>
  )
}

export default Breadcrumbs
